import { useRef } from 'react';

import { InboxOutlined } from '@ant-design/icons';
import { DirectUpload } from '@rails/activestorage';
import type { UploadRequestOption } from '@rc-component/upload/lib/interface';
import type { UploadFile, UploadProps } from 'antd';
import { App, Upload } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import { Controller, useFormContext } from 'react-hook-form';

import { useUpdateAuthDataMutation } from '@/api/endpoints/casdoorAuth';
import { casdoorMutex } from '@/api/queries/casdoorMutex';
import { ENVS, HTTPStatusCode } from '@/shared/consts';
import { getCasdoorAccessToken, getCasdoorRefreshToken, logoutUser, saveCasdoorTokens } from '@/shared/helpers';

import { FieldValidationMessage } from '../FieldValidationMessage/FieldValidationMessage';

import styles from './Field.module.scss';

interface FileResponse {
  signed_id: string;
  metadata?: {
    public_url: string;
  };
  filename: string;
}

interface IUploadProps extends UploadProps {
  fieldName: string;
  requestCallback?: (blob: File) => void;
  changeCallback?: VoidFunction;
}

const MAX_RETRIES_COUNT = 2;

export const ImageUploadField = ({
  fieldName,
  requestCallback,
  changeCallback,
  accept,
  ...restProps
}: IUploadProps) => {
  const { control, clearErrors, setError } = useFormContext();

  const [updateAuthData, { isLoading: isRefetchingToken }] = useUpdateAuthDataMutation();

  const { message } = App.useApp();

  const maxRetriesCount = useRef(MAX_RETRIES_COUNT);

  const refreshTokenAndReuploadImage = async (options: UploadRequestOption<FileResponse>) => {
    if (casdoorMutex.isLocked()) {
      await casdoorMutex.waitForUnlock();
      void handleCustomRequest(options);

      return;
    }

    const release = await casdoorMutex.acquire();

    try {
      const refreshToken = getCasdoorRefreshToken();
      const { access_token: casdoorAccessToken, refresh_token: newCasdoorRefreshToken } = await updateAuthData({
        refreshToken,
      }).unwrap();

      saveCasdoorTokens({ casdoorAccessToken, casdoorRefreshToken: newCasdoorRefreshToken });

      void handleCustomRequest(options);
    } catch {
      logoutUser();
    } finally {
      release();
    }
  };

  const handleCustomRequest = async (options: UploadRequestOption<FileResponse>) => {
    await casdoorMutex.waitForUnlock();

    const accessToken = getCasdoorAccessToken();

    if (!accessToken) {
      return;
    }

    const { file, onError, onSuccess } = options;

    const directUpload = new DirectUpload(
      file as File,
      `${ENVS.BACKEND_URL}/rails/active_storage/direct_uploads`,
      undefined,
      {
        Authorization: `Bearer ${accessToken}`,
      },
    );

    directUpload.create((error, blob) => {
      if (error) {
        const errMsg = typeof error === 'string' ? error : error.message;
        const statusCode = Number(errMsg.replace(/^.*status:\s?(\d+)$/i, '$1')) as HTTPStatusCode;

        if (statusCode === HTTPStatusCode.UnAuthorized && maxRetriesCount.current > 0) {
          maxRetriesCount.current -= 1;

          void refreshTokenAndReuploadImage(options);

          return;
        }

        onError?.(error);

        message.error('Произошла ошибка при загрузке изображения');
      } else {
        maxRetriesCount.current = MAX_RETRIES_COUNT;

        onSuccess?.(blob!);

        requestCallback?.(file as File);
      }
    });
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { onChange, value, ...restFieldProps }, fieldState: { invalid: isInvalid, error } }) => (
        <div className={styles['field-wrapper']}>
          <Upload.Dragger
            {...restProps}
            {...restFieldProps}
            accept={accept}
            beforeUpload={(file) => {
              const acceptFileTypes =
                accept && typeof accept === 'string' ? accept?.split(',') : ['image/png', 'image/jpeg', 'image/jpg'];
              const isPermittedFileType = acceptFileTypes.includes(file.type);

              if (!isPermittedFileType) {
                setError(fieldName, {
                  type: 'custom',
                  message: 'Загрузите изображение в формате png, jpg, или jpeg',
                });
              }

              const sizeInMb = file.size / 1024 / 1024;
              const isValidImageSize = sizeInMb < 5;

              if (!isValidImageSize) {
                setError(fieldName, {
                  type: 'custom',
                  message: 'Загрузите изображение размером менее 5 Мб',
                });
              }

              const isValidFile = isPermittedFileType && isValidImageSize;

              if (isValidFile) {
                clearErrors(fieldName);
              }

              return isValidFile;
            }}
            onChange={({ file: { status, response, name } }: UploadChangeParam<UploadFile<FileResponse>>) => {
              if (status === 'done') {
                onChange({
                  url: response?.metadata?.public_url,
                  signed_id: response?.signed_id,
                  filename: name,
                });
                changeCallback?.();
              }
            }}
            customRequest={handleCustomRequest}
            fileList={value ? [value] : undefined}
            style={isInvalid ? { borderColor: '#ff4d4f' } : {}}
            disabled={isRefetchingToken}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Изображение</p>
            <p className="ant-upload-hint">Выберите файл </p>
          </Upload.Dragger>
          {isInvalid && <FieldValidationMessage message={error?.message} />}
        </div>
      )}
    />
  );
};
